import { BarChart3, Scale, type LucideIcon } from "lucide-react";

/**
 * Single source of truth for the /resurser/jamforelser overview.
 *
 * Every comparison that has a route under `routes.comparisons.*` MUST have an
 * entry here, so the overview can never silently omit a published comparison.
 * A contract test enforces this 1:1 mapping.
 *
 * Translatable fields carry both the i18n key and a Swedish fallback; the
 * overview resolves them with `t(key, fallback)`.
 */
export interface ComparisonI18n {
  key: string;
  fallback: string;
}

export interface ComparisonMeta {
  /** routes.ts key under `comparisons.*`, used to derive the localized URL. */
  routeKey: string;
  icon: LucideIcon;
  readingTime: string;
  /** ISO date (YYYY-MM-DD); the overview sorts by this, newest first. */
  publishDate: string;
  /** Systems compared; not translated (brand/tool names). */
  compared: string[];
  /**
   * Optional — omit for complementary/partner guides where there is no
   * "winner" (e.g. a care system alongside Caire). When set, the overview renders a
   * "Vinnare: {winner}" badge; when omitted, no badge is shown.
   */
  winner?: string;
  title: ComparisonI18n;
  description: ComparisonI18n;
}

export const COMPARISONS: ComparisonMeta[] = [
  {
    routeKey: "comparisons.routePatternsVsDynamic",
    icon: Scale,
    readingTime: "4 min",
    publishDate: "2026-10-02",
    compared: ["Slingplanering", "Dynamisk besöksplanering"],
    title: {
      key: "resources:comparisons.routePatterns.title",
      fallback: "Slingplanering och dynamisk besöksplanering",
    },
    description: {
      key: "resources:comparisons.routePatterns.description",
      fallback:
        "Från återanvända slingor till besöksplanering som följer förändringar automatiskt, med kontinuitet, kompetens och restid.",
    },
  },
  {
    routeKey: "comparisons.schedulingSystems",
    icon: BarChart3,
    readingTime: "12 min",
    publishDate: "2025-05-25",
    compared: ["Caire", "Excel", "Traditionella planeringssystem"],
    winner: "Caire",
    title: {
      key: "resources:comparisons.scheduling_systems.title",
      fallback: "Hemtjänstschemaläggningssystem",
    },
    description: {
      key: "resources:comparisons.scheduling_systems.description",
      fallback:
        "En objektiv jämförelse av ledande schemaläggningssystem för hemtjänsten och deras funktioner.",
    },
  },
  {
    routeKey: "comparisons.excelVsAI",
    icon: Scale,
    readingTime: "6 min",
    publishDate: "2025-05-25",
    compared: ["Excel", "AI-schemaläggning"],
    winner: "AI-schemaläggning",
    title: {
      key: "resources:comparisons.excel_ai.title",
      fallback: "Excel vs AI-schemaläggning",
    },
    description: {
      key: "resources:comparisons.excel_ai.description",
      fallback:
        "Varför AI-driven schemaläggning överträffar traditionell Excel-baserad planering.",
    },
  },
];
